import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { Input } from '../components/ui/atoms/Input';
import { Label } from '../components/ui/label';
import { Button } from '@/components/ui/atoms/Button';
import {
  AuthClientError,
  authReturnPath,
  GOOGLE_CONNECTION,
  loginEmailPassword,
  MICROSOFT_CONNECTION,
  signupIntent,
} from '../services/auth.service';
import { GoogleIcon, MicrosoftIcon } from '../components/auth/SocialIcons';
import { AuthLayout } from '../components/auth/AuthLayout';

export const LoginPage: React.FC = () => {
  const { t } = useTranslation(['auth', 'common']);
  const location = useLocation();
  const tk = (base: string): string => t(`auth:login.${base}` as never);
  const { loginWithRedirect } = useAuth0();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formValid = email.trim().length > 3 && password.length > 0;
  const returnTo =
    new URLSearchParams(location.search).get('returnTo') ?? undefined;

  const VALUE_PROPS = [
    { text: tk('value_prop_1') },
    { text: tk('value_prop_2') },
    { text: tk('value_prop_3') },
    { text: tk('value_prop_4') },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formValid || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const ticket = await loginEmailPassword({ email, password });
      signupIntent.clear();
      await loginWithRedirect({
        appState: { returnTo: returnTo ?? '/auth-callback' },
        authorizationParams: {
          login_ticket: ticket.login_ticket,
        },
      });
    } catch (err) {
      const msg =
        err instanceof AuthClientError ? err.message : t('auth:login.wrong_credentials');
      setError(msg);
      toast.error(msg);
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      planTitle={tk('plan_title')}
      planTagline={tk('plan_tagline')}
      planFeatures={VALUE_PROPS}
      description={tk('plan_description')}
    >
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-dark-invirtus">
            {t('auth:login.title')}
          </h1>
          <p className="mt-1 text-sm text-dark-invirtus/60">
            {tk('subtitle')}
          </p>
        </div>

        <div className="space-y-3">
          <Button
            type="outlined"
            intent="muted"
            size="md"
            label={t('auth:login.continue_google')}
            icon={<GoogleIcon />}
            className="w-full"
            onClick={() => {
              signupIntent.clear();
              authReturnPath.save(window.location.pathname + window.location.search);
              loginWithRedirect({
                appState: { returnTo: '/auth-callback' },
                authorizationParams: { connection: GOOGLE_CONNECTION },
              });
            }}
          />
          <Button
            type="outlined"
            intent="muted"
            size="md"
            label={t('auth:login.continue_microsoft')}
            icon={<MicrosoftIcon />}
            className="w-full"
            onClick={() => {
              signupIntent.clear();
              authReturnPath.save(window.location.pathname + window.location.search);
              loginWithRedirect({
                appState: { returnTo: '/auth-callback' },
                authorizationParams: { connection: MICROSOFT_CONNECTION },
              });
            }}
          />
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-light-lila" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="bg-white px-3 text-dark-invirtus/50">
              {t('auth:login.or_with_email')}
            </span>
          </div>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit} noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="login-email" className="text-dark-invirtus">
              {t('common:forms.email')}
            </Label>
            <Input
              id="login-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('common:forms.email_placeholder')}
              rounded="lg"
            />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="login-password" className="text-dark-invirtus">
                {t('common:forms.password')}
              </Label>
              <Link
                to="/forgot-password"
                className="text-xs font-medium text-purple hover:underline"
              >
                {t('auth:login.forgot')}
              </Link>
            </div>
            <Input
              id="login-password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              rounded="lg"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}

          <Button
            label={submitting ? t('auth:login.signing_in') : t('auth:login.sign_in')}
            type="contained"
            intent="secondary"
            htmlType="submit"
            disabled={!formValid || submitting}
            loading={submitting}
            className="w-full"
          />
        </form>

        <p className="text-center text-sm text-dark-invirtus/60">
          {t('auth:login.new_here')}{' '}
          <Link
            to="/signup?plan=free_trial"
            className="font-medium text-purple hover:underline"
          >
            {t('auth:login.create_account')}
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
};

export default LoginPage;
